import * as React from "react";

import { authenticationParametersChangePassword, getAccountIdentifier, pca } from "authProvider";
import { DebugContainer } from "components/Debug/DebugContainer";
import { Drawer } from "components/Drawer/Drawer";
import { InitializingContainer } from "components/Initializing/InitializingContainer";
import NavigateExternal from "components/NavigateExternal/NavigateExternal";
import Ripple from "components/Ripple/Ripple";
import { NewEncounterContext, NewEncounterProvider } from "contexts/NewEncounterContext";

import { NewEncounterDialog } from "../../scenes/SearchEncounters/components/Dialogs/NewEncounterDialog";

import LogoutDialog from "./components/LogoutDialog/LogoutDialog";
import { NoticeList } from "./components/NoticeList/NoticeList";
import Snackbar from "./components/Snackbar";
import TopNavigation from "./components/TopNavigation/TopNavigation";

import type { ReactNode } from "react";
import type * as History from "history";
import type { EncounterState, ErrorDetails, RecoveredEncountersState, UserState } from "models";
import type { AnnouncementsState } from "models/announcements";
import type { ChoiceListsState } from "models/patientEncounter";

import "./AppWrapper.scss";

interface AppWrapperProps {
  // from container
  errors: ErrorDetails[];
  choiceLists: ChoiceListsState;
  user: UserState;
  clearApplication: () => void;
  logoutUser: (accountIdentifier: string, unsavedChanges?: boolean) => void;
  clearErrors: () => void;
  buildNewEncounterChoiceLists: (choiceLists: ChoiceListsState, facility: string) => void;
  saveUserPreference: (preference: string, value: any) => void;
  getFacilitiesChoiceList: () => void;
  getPracticeChoiceList: () => void;
  // props
  handleCreateNewEncounter?: (
    choiceLists: ChoiceListsState,
    mrn?: string,
    facility?: string,
    encounterType?: string,
  ) => void;
  // page can provide this callback to check about not saved changes and show warning
  hasChangesCallback?: (to: History.History.LocationDescriptor, clickHandler?: () => void) => void;
  // page can provide Promise to close something before logout
  clearPageBeforeLogoutPromise?: () => Promise<boolean>;
  isModal?: boolean;
  isStandalone?: boolean;
  isEncountersPage?: boolean;
  skipInit?: boolean;
  // manual props
  isErrorMode?: boolean;
  encounter: EncounterState;
  announcements: AnnouncementsState;
  dismissAnnouncement: (id: string) => void;
  recoveredEncounters: RecoveredEncountersState;
  refreshRecoveredEncountersCount: () => void;
  children: ReactNode;
}

enum ActiveDrawerContent {
  NONE,
  DEBUG,
  ERRORS,
}

enum DrawerWidth {
  STANDARD = 600,
  NARROW = 300,
  WIDE = 900,
}

interface AppWrapperState {
  showDrawer: boolean;
  activeDrawerContent: ActiveDrawerContent;
  drawerWidth: number;
  showLogoutDialog: boolean;
}

export class AppWrapper extends React.Component<AppWrapperProps, AppWrapperState> {
  static contextType = NewEncounterContext;
  context!: React.ContextType<typeof NewEncounterContext>;

  constructor(props: AppWrapperProps) {
    super(props);
    this.state = {
      showDrawer: false,
      activeDrawerContent: ActiveDrawerContent.ERRORS,
      drawerWidth: DrawerWidth.NARROW,
      showLogoutDialog: false,
    };
  }

  render() {
    const isErrorMode = this.props.isErrorMode || false;
    const errorCount =
      this.props.errors && this.props.errors.length > 0 ? this.props.errors.length : undefined;
    return (
      <InitializingContainer
        skipInit={this.props.skipInit || this.props.isModal || this.props.isStandalone}
      >
        <Ripple disabled={this.props.user.preferences.AnimationDisabled}>
          <NavigateExternal
            saveUserPreference={this.props.saveUserPreference}
            hideExternalLinkWarning={this.props.user.preferences.HideExternalLinkWarning}
          >
            <Snackbar />
            <LogoutDialog
              show={this.state.showLogoutDialog}
              onLogoutNoClick={this.onLogoutNoClick}
              onLogoutYesClick={this.onLogoutYesClick}
            />
            <Drawer
              width={this.state.drawerWidth}
              expanded={this.state.showDrawer}
              content={this.getDrawerContents}
              onOverlayClick={this.onCloseDrawer}
            />
            <NewEncounterProvider
              choiceLists={this.props.choiceLists}
              onCreateNewEncounter={this.createNewEncounterClick}
              buildNewEncounterChoiceLists={this.props.buildNewEncounterChoiceLists}
              getFacilitiesChoiceList={this.props.getFacilitiesChoiceList}
              getPracticeChoiceList={this.props.getPracticeChoiceList}
              encounterOpened={
                this.props.encounter.inProgress.id !== null &&
                this.props.encounter.inProgress.id !== "0"
              }
            >
              <div className={`app${this.props.isStandalone ? " standalone" : ""}`}>
                <TopNavigation
                  announcements={this.props.announcements}
                  encounter={this.props.encounter}
                  encounterOpened={
                    this.props.encounter.inProgress.id !== null &&
                    this.props.encounter.inProgress.id !== "0"
                  }
                  errorCount={errorCount}
                  hasChangesCallback={this.props.hasChangesCallback}
                  isErrorMode={isErrorMode}
                  isModal={this.props.isModal}
                  isStandalone={this.props.isStandalone}
                  skipInit={this.props.skipInit}
                  onChangePasswordClick={this.handleChangePassword}
                  onDismissAnnouncement={this.props.dismissAnnouncement}
                  onLogoutClick={this.handleLogout}
                  onShowErrorsClick={this.onShowErrorsClick}
                  recoveredEncountersCount={this.props.recoveredEncounters.recoveredEncountersTotal}
                  refreshRecoveredEncountersCount={this.props.refreshRecoveredEncountersCount}
                  user={this.props.user}
                />
                <div className="contents-wrapper">{this.props.children}</div>
                <NewEncounterDialog />
              </div>
            </NewEncounterProvider>
          </NavigateExternal>
        </Ripple>
      </InitializingContainer>
    );
  }

  private getDrawerContents = () => {
    switch (this.state.activeDrawerContent) {
      case ActiveDrawerContent.DEBUG:
        return {
          headingText: "Debug",
          content: <DebugContainer />,
        };
      case ActiveDrawerContent.ERRORS:
        return {
          headingText: "Alerts",
          content: (
            <NoticeList notices={this.props.errors} onClickClear={this.onClearErrorsClick} />
          ),
        };
      default:
      case ActiveDrawerContent.NONE:
        break;
    }
    return null;
  };

  private onShowErrorsClick = () => {
    this.setState({
      showDrawer: true,
      activeDrawerContent: ActiveDrawerContent.ERRORS,
      drawerWidth: DrawerWidth.STANDARD,
    });
  };

  private onCloseDrawer = () => {
    this.setState({
      showDrawer: false,
      activeDrawerContent: ActiveDrawerContent.NONE,
      drawerWidth: DrawerWidth.NARROW,
    });
  };

  private onClearErrorsClick = () => {
    this.setState(
      {
        showDrawer: false,
      },
      () => {
        this.props.clearErrors();
      },
    );
  };

  // logout button clicked
  private handleLogout = () => {
    if (!this.props.hasChangesCallback) {
      this.performShowLogoutWarning();
      return;
    }

    this.props.hasChangesCallback("/logout", this.performShowLogoutWarning);
  };

  // show logout warning
  private performShowLogoutWarning = () => {
    this.setState({ showLogoutDialog: true });
  };

  // user press Yes on logout dialog:
  //    force active page to perform close action if it has something to be closed
  //    logout user after this
  private onLogoutYesClick = () => {
    localStorage.setItem("showCopyrightModal", "yes");

    // no need to close active page -> logout
    if (!this.props.clearPageBeforeLogoutPromise) {
      this.logoutUser();
      return;
    }

    const closePromise = this.props.clearPageBeforeLogoutPromise();

    this.setState({ showLogoutDialog: false }, () =>
      closePromise.then(() => {
        this.logoutUser();
      }),
    );
  };

  // real logout
  private logoutUser = () => {
    this.props.clearApplication();
    this.setState({ showLogoutDialog: false });

    if (!window.useAzureADAuth) {
      return;
    }

    const accountIdentifier = getAccountIdentifier();

    // logout current user from all tabs
    this.props.logoutUser(accountIdentifier);
  };

  private onLogoutNoClick = () => {
    this.setState({ showLogoutDialog: false });
  };

  // change password button clicked
  private handleChangePassword = () => {
    if (!this.props.hasChangesCallback) {
      this.performChangePassword();
      return;
    }

    this.props.hasChangesCallback("/logout", this.performChangePassword);
  };

  // prepare to relogin to the change password page:
  //    force active page to perform close action if it has something to be closed
  //    show change password page after this
  private performChangePassword = () => {
    // no need to close active page -> change password
    if (!this.props.clearPageBeforeLogoutPromise) {
      this.changePassword();
      return;
    }

    const closePromise = this.props.clearPageBeforeLogoutPromise();

    return closePromise.then(() => this.changePassword());
  };

  // redirect to the change password page
  private changePassword = () => {
    // encounter opened, use popup to change password
    if (this.props.encounter.inProgress.id !== null && this.props.encounter.inProgress.id !== "0") {
      return pca.loginPopup(authenticationParametersChangePassword).then(() => "");
    }

    // no encounter opened, redirect using current page
    return pca.loginRedirect(authenticationParametersChangePassword).then(() => "");
  };

  private createNewEncounterClick = (
    choiceLists: ChoiceListsState,
    mrn?: string,
    facility?: string,
    encounterType?: string,
    accountNumber?: string,
  ) => {
    if (this.props.handleCreateNewEncounter) {
      this.props.handleCreateNewEncounter(choiceLists, mrn, facility, encounterType, accountNumber);
    }
  };
}
